import type { ClockState } from "../../../domain/clock";
import type { LocalEvent } from "../../../offline/model";

/**
 * A clock step being stored, or stored but not yet seen by the screen (F-104). Until the screen
 * shows its result, a further tap is checked against the state before the step and would store the
 * same step twice.
 */
export interface ClockStep {
  /** Clock state the step started from. */
  readonly fromState: ClockState;
  /** Identifier of the stored event, or `null` while it is being stored. */
  readonly eventId: string | null;
}

/**
 * Returns whether the screen shows the result of a step: the clock state moved on (every valid step
 * changes it, also when another device or the server changed it meanwhile) or the stored event is
 * among the local events.
 *
 * @param step - The step.
 * @param state - Clock state the screen shows.
 * @param events - Local events the screen shows.
 * @returns `true` once a further tap is checked against the result of the step.
 */
export function clockStepShown(
  step: ClockStep,
  state: ClockState,
  events: readonly LocalEvent[],
): boolean {
  const shown =
    state !== step.fromState ||
    (step.eventId !== null && events.some((event) => event.id === step.eventId));

  return shown;
}
